import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  ShoppingBag, 
  Search, 
  Menu, 
  X, 
  PhoneCall, 
  ShieldCheck, 
  Layers, 
  Sparkles,
  ChevronDown,
  UserCheck
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useAuth } from "@/context/AuthContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

export default function Header({ onOpenCart }) {
  const { totalItemsCount } = useCart();
  const { settings } = useSiteSettings();
  const { isAuthenticated } = useAuth();
  const { customer, isSignedIn, signInWithGoogle, logout } = useCustomerAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "All Laces & Sets", path: "/shop" },
    { name: "Categories", path: "/categories" },
    { name: "Wholesale Orders", path: "/wholesale", highlight: true },
    { name: "Track Order", path: "/track-order" },
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const categories = [
    "Diamond Zari Sitara Lace",
    "Medium Size Diamond Sitara",
    "Zero Size Diamond Sitara",
    "Organza Lace",
    "Net Milky Lace",
    "Bridal Lace",
    "Sequence Galla",
    "Fashion Accessories",
    "Milky White Black Lace",
    "Milky Color Lace"
  ];

  return (
    <header className="sticky top-0 z-40 w-full bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#E5E0D8]">
      {/* Top Luxury Announcement Bar */}
      <div className="bg-[#1C1917] text-[#FAF8F5] text-xs py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
          <p className="flex items-center justify-center gap-1.5 font-medium tracking-wide">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>{settings.announcement_text}</span>
          </p>
          <div className="flex items-center gap-4 text-[11px] text-[#E6C687]">
            <a
              href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Hello%20Anwer%20Fancy%20Lace,%20I%20have%20an%20inquiry.`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-link"
              className="hover:underline flex items-center gap-1"
            >
              <span>WhatsApp: {settings.whatsapp_number}</span>
            </a>
            <span className="hidden md:inline text-stone-600">|</span>
            <span className="text-stone-400 text-[10px] italic">Wholesale + Retail across Pakistan</span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          
          {/* Mobile Menu Button */}
          <button
            type="button"
            data-testid="mobile-menu-toggle-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-stone-800 hover:text-[#B8860B] focus:outline-none"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Brand Logo & Tagline */}
          <Link to="/" data-testid="brand-logo-link" className="flex flex-col items-start select-none">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]"></span>
              <span className="font-brand text-2xl sm:text-3xl font-bold tracking-wider text-[#1C1917]">
                ANWER <span className="text-[#B8860B] font-serif font-normal italic">FANCY LACE</span>
              </span>
            </div>
            <span className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-medium pl-4 -mt-1">
              Pakistan's Premium Lace Brand
            </span>
          </Link>

          {/* Search Bar (Desktop) */}
          <form 
            onSubmit={handleSearchSubmit} 
            className="hidden md:flex flex-1 max-w-md mx-6 relative"
          >
            <input
              type="text"
              data-testid="header-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search diamond sitara, organza, bridal sets..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-[#F5F2EB] border border-[#E5E0D8] rounded-full focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
            />
            <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <button 
              type="submit"
              data-testid="header-search-submit-btn"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] rounded-full text-xs font-medium transition"
            >
              Search
            </button>
          </form>

          {/* Right Action Icons */}
          <div className="flex items-center gap-3 sm:gap-4">
            
            {/* Direct WhatsApp Call/Chat */}
            <a
              href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Assalam%20o%20Alaikum,%20I%20want%20to%20order%20fancy%20lace%20sets.`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-cta-button"
              className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#996515] hover:bg-[#D4AF37] hover:text-white text-xs font-semibold tracking-wide transition"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              <span>Order on WhatsApp</span>
            </a>

            {/* Customer Sign-in / Account */}
            {!isSignedIn ? (
              <button
                type="button"
                onClick={signInWithGoogle}
                data-testid="header-signin-btn"
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300 bg-white hover:bg-[#FAF8F5] text-stone-800 text-xs font-semibold transition"
              >
                <UserCheck className="w-3.5 h-3.5 text-[#996515]" />
                <span>Sign in</span>
              </button>
            ) : (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setAccountMenuOpen((o) => !o)}
                  data-testid="header-account-btn"
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  className="flex items-center gap-2 px-2 py-1 rounded-full border border-stone-200 bg-white hover:bg-[#FAF8F5] transition"
                >
                  {customer?.picture ? (
                    <img src={customer.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[#1C1917] text-[#D4AF37] flex items-center justify-center text-[10px] font-bold">
                      {(customer?.name || customer?.email || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="hidden md:inline text-xs font-semibold text-stone-800 max-w-[7rem] truncate">
                    {customer?.name?.split(" ")[0] || customer?.email}
                  </span>
                  <ChevronDown className="w-3 h-3 text-stone-500" />
                </button>
                {accountMenuOpen && (
                  <div
                    data-testid="header-account-menu"
                    className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-[#E5E0D8] shadow-lg overflow-hidden text-xs z-50"
                    role="menu"
                    onMouseLeave={() => setAccountMenuOpen(false)}
                  >
                    <Link
                      to="/account"
                      data-testid="header-menu-account"
                      onClick={() => setAccountMenuOpen(false)}
                      className="block px-4 py-2.5 hover:bg-[#FAF8F5] text-stone-800 font-semibold"
                    >
                      My Account
                    </Link>
                    <Link
                      to="/my-orders"
                      data-testid="header-menu-orders"
                      onClick={() => setAccountMenuOpen(false)}
                      className="block px-4 py-2.5 hover:bg-[#FAF8F5] text-stone-800 font-semibold"
                    >
                      My Orders
                    </Link>
                    <Link
                      to="/track-order"
                      onClick={() => setAccountMenuOpen(false)}
                      className="block px-4 py-2.5 hover:bg-[#FAF8F5] text-stone-800 font-semibold"
                    >
                      Track Order
                    </Link>
                    <button
                      type="button"
                      data-testid="header-menu-signout"
                      onClick={() => { setAccountMenuOpen(false); logout(); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-rose-50 text-rose-700 font-semibold border-t border-stone-100"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Cart Button */}
            <Link
              to="/cart"
              data-testid="header-cart-button"
              className="relative p-2.5 rounded-full bg-[#F5F2EB] hover:bg-[#E6C687]/30 border border-[#E5E0D8] text-stone-800 transition flex items-center justify-center"
              aria-label="View Shopping Cart"
            >
              <ShoppingBag className="w-5 h-5 text-stone-800" />
              {totalItemsCount > 0 && (
                <span 
                  data-testid="cart-badge-count" 
                  className="absolute -top-1.5 -right-1.5 bg-[#B8860B] text-white text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-sm"
                >
                  {totalItemsCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Desktop Navigation Links */}
        <nav className="hidden lg:flex items-center justify-center space-x-8 py-3 border-t border-[#E5E0D8]/60 text-sm font-medium tracking-wide">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`transition-colors py-1 relative ${
                  isActive 
                    ? "text-[#B8860B] font-semibold" 
                    : link.highlight 
                      ? "text-[#B76E79] font-semibold hover:text-[#996515]"
                      : "text-stone-700 hover:text-[#B8860B]"
                }`}
              >
                {link.name}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37] rounded-full"></span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#FAF8F5] border-b border-[#E5E0D8] px-4 pt-3 pb-6 space-y-4 shadow-xl">
          {/* Mobile Search */}
          <form onSubmit={handleSearchSubmit} className="relative">
            <input
              type="text"
              data-testid="mobile-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search laces, complete sets..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-[#E5E0D8] rounded-full focus:outline-none focus:border-[#D4AF37]"
            />
            <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          </form>

          {/* Mobile Links */}
          <div className="flex flex-col space-y-2 pt-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  location.pathname === link.path
                    ? "bg-[#E6C687]/20 text-[#B8860B] font-semibold"
                    : "text-stone-800 hover:bg-stone-100"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="pt-3 border-t border-stone-200">
            <a
              href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Assalam%20o%20Alaikum,%20I%20want%20to%20inquire%20about%20wholesale%20laces.`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-direct-btn"
              className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-center text-sm font-semibold flex items-center justify-center gap-2 shadow-sm"
            >
              <PhoneCall className="w-4 h-4" />
              <span>Direct WhatsApp Inquiry</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
